// SCR-STAFF-418 — صف بررسی همگام‌سازی آفلاین (ARD-offline §۳؛ AP-OFF-01): تعارض‌ها با توضیح، اقدام پیشنهادی و اقدام‌های مجاز (اجرای دوباره،
// صدور با قیمت سرور، «فرد متفاوت است»، پرونده موجود، کنار گذاشتن با یادداشت) در دامنه شعبه کاربر (bil.sync_conflict.manage).
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type BilOfflineConflictView,
  type BilOfflineOperationStatus,
  type BilOfflineResolution,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Amount } from './common';

export const SCR_STAFF_418 = 'SCR-STAFF-418';

const STATUSES: readonly BilOfflineOperationStatus[] = ['Conflict', 'Resolved', 'Dismissed', 'Applied'];

export const syncKeys = {
  conflicts: (tenantId: string | undefined, status: string) => ['billing', tenantId ?? null, 'sync', 'conflicts', status] as const,
  all: (tenantId: string | undefined) => ['billing', tenantId ?? null, 'sync'] as const,
};

export function SyncConflictsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [status, setStatus] = useState<BilOfflineOperationStatus>('Conflict');
  const [dismissing, setDismissing] = useState<BilOfflineConflictView | null>(null);
  const [note, setNote] = useState('');
  const conflicts = useInfiniteQuery({
    queryKey: syncKeys.conflicts(session?.tenantId, status),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(tenantApi.GET('/api/v1/sync/conflicts', { params: { query: { status, cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const resolve = useMutation({
    mutationFn: ({ id, resolution, text }: { id: string; resolution: BilOfflineResolution; text?: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/sync/conflicts/{id}:resolve', {
          params: { path: { id } },
          body: { resolution, patientId: null, note: text?.trim() || null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setDismissing(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: syncKeys.all(session?.tenantId) });
    },
  });
  const writable = access.accessMode === 'Full';
  const columns: DataTableColumn<BilOfflineConflictView>[] = [
    { id: 'occurred', headerKey: 'bil:offline.columns.occurred', cell: (row) => <JalaliDate value={row.occurredAt} withTime /> },
    { id: 'kind', headerKey: 'bil:offline.columns.kind', cell: (row) => t(`bil:offline.kind.${row.kind}`) },
    { id: 'patient', headerKey: 'bil:offline.columns.patient', cell: (row) => row.summary.patientName ?? '—' },
    {
      id: 'conflict',
      headerKey: 'bil:offline.columns.conflict',
      cell: (row) => (
        <Stack spacing={0.5} data-testid="sync-conflict-row" data-code={row.conflictCode ?? ''}>
          <Typography variant="body2">{row.conflictCode ? t(`bil:offline.conflict.${row.conflictCode}`) : t(`bil:offline.entry_status.${row.status.toLowerCase()}`)}</Typography>
          {row.suggestedAction ? (
            <Typography variant="caption" color="text.secondary">
              {t('bil:offline.suggested')}: {t(`bil:offline.action.${row.suggestedAction}`)}
            </Typography>
          ) : null}
          {row.detail['errorCode'] ? (
            <Typography variant="caption" color="text.secondary" dir="ltr">
              {row.detail['errorCode']}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'amount',
      headerKey: 'bil:offline.columns.amount',
      cell: (row) => (row.summary.amount ? <Amount money={row.summary.amount} /> : '—'),
    },
    { id: 'actor', headerKey: 'bil:offline.columns.actor', cell: (row) => `${row.actorName ?? '—'} · ${row.deviceId}` },
    {
      id: 'actions',
      headerKey: 'bil:offline.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {row.allowedResolutions.map((resolution) => (
            <Button
              key={resolution}
              size="small"
              color={resolution === 'Dismiss' ? 'inherit' : 'primary'}
              disabled={!writable || resolve.isPending}
              onClick={() => (resolution === 'Dismiss' ? setDismissing(row) : resolve.mutate({ id: row.id, resolution }))}
              data-testid={`sync-resolve-${resolution}`}
            >
              {t(`bil:offline.resolution.${resolution}`)}
            </Button>
          ))}
        </Stack>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_418}
      titleKey="bil:offline.conflicts_title"
      subtitleKey="bil:offline.conflicts_subtitle"
      filters={
        <TextField
          select
          size="small"
          label={t('bil:offline.columns.status')}
          value={status}
          onChange={(event) => setStatus(event.target.value as BilOfflineOperationStatus)}
          sx={{ minWidth: 220 }}
          slotProps={{ htmlInput: { 'data-testid': 'sync-conflicts-status' } }}
        >
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`bil:offline.entry_status.${value.toLowerCase()}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      {resolve.error && !dismissing ? <ErrorState error={resolve.error} compact /> : null}
      <DataTable
        captionKey="bil:offline.conflicts_title"
        columns={columns}
        rows={conflicts.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={conflicts.isPending}
        error={conflicts.error}
        onRetry={() => void conflicts.refetch()}
        hasMore={conflicts.hasNextPage}
        onLoadMore={() => void conflicts.fetchNextPage()}
        isFetchingMore={conflicts.isFetchingNextPage}
        emptyTitleKey="bil:offline.conflicts_empty"
      />
      {dismissing ? (
        <FormDialog
          open
          titleKey="bil:offline.resolution.Dismiss"
          onSubmit={() => note.trim() && resolve.mutate({ id: dismissing.id, resolution: 'Dismiss', text: note })}
          onClose={() => setDismissing(null)}
          isDirty={note !== ''}
          isSubmitting={resolve.isPending}
          submitError={resolve.error}
        >
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            required
            multiline
            minRows={2}
            label={t('bil:offline.note')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'sync-dismiss-note' } }}
          />
        </FormDialog>
      ) : null}
    </ListPage>
  );
}
